import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, FileText, RefreshCw, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";

const CareerApplications = () => {
  const { showErrorToast, showSuccessToast } = useToast();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visiblePages = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [page, totalPages]);

  const fetchApplications = async (
    pageNumber = page,
    limit = pageSize,
    searchTerm = appliedSearch,
  ) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pageNumber),
        limit: String(limit),
      });
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      const response = await apiClient.get(`/career/applications?${params.toString()}`);
      const responseData = response.data?.data || {};
      setApplications(responseData.applications || []);
      setTotal(Number(responseData.total) || 0);
      setPage(Number(responseData.page) || pageNumber);
      setPageSize(Number(responseData.limit) || limit);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load applications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(1, 20, "");
  }, []);

  const handleSearch = () => {
    const normalizedSearch = search.trim();
    setAppliedSearch(normalizedSearch);
    fetchApplications(1, pageSize, normalizedSearch);
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
    fetchApplications(1, pageSize, "");
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    fetchApplications(nextPage, pageSize, appliedSearch);
  };

  const handlePageSizeChange = (value) => {
    const nextPageSize = Number(value);
    setPageSize(nextPageSize);
    fetchApplications(1, nextPageSize, appliedSearch);
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    setUpdatingStatus(applicationId);
    try {
      await apiClient.patch(`/career/applications/${applicationId}/status`, { status: newStatus });
      showSuccessToast("Status updated");
      setApplications((prev) =>
        prev.map((a) => a.id === applicationId ? { ...a, status: newStatus } : a)
      );
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Career Applications</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Review applications submitted from the frontend career form.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              placeholder="Search name, email, position..."
              aria-label="Search career applications"
              className="h-10 rounded-lg pl-10 pr-10"
            />
            {search ? (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Clear application search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <Button type="button" variant="outline" className="rounded-lg" onClick={handleSearch}>
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => fetchApplications(page, pageSize, appliedSearch)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No applications found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Applicant</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Position</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">City</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Message</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Resume</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {applications.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <div className="font-semibold text-slate-800">{item.name}</div>
                      <div>{item.email}</div>
                      <div>{item.contact_no}</div>
                      <div className="mt-1 text-xs text-slate-400">{String(item.created_at).slice(0, 19).replace("T", " ")}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{item.position}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{item.city}</td>
                    <td className="px-5 py-4 text-sm">
                      <Select
                        value={item.status || "pending"}
                        disabled={updatingStatus === item.id}
                        onValueChange={(value) => handleStatusChange(item.id, value)}
                      >
                        <SelectTrigger className="min-w-36">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="shortlisted">Shortlisted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="hired">Hired</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600"><div className="line-clamp-3">{item.message || "-"}</div></td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <a href={item.resume_url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                        <FileText className="mr-2 h-3.5 w-3.5" />
                        Open Resume
                        <ExternalLink className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-slate-500">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} applications
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Rows</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-9 w-20" aria-label="Applications per page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-lg px-3"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || isLoading}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>

                <div className="hidden items-center gap-1 md:flex">
                  {visiblePages.map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      type="button"
                      variant={pageNumber === page ? "default" : "outline"}
                      className="h-9 min-w-9 rounded-lg px-3"
                      onClick={() => handlePageChange(pageNumber)}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-lg px-3"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || isLoading}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CareerApplications;
